import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import ChatWindow from "../components/ChatWindow";
import InputBar from "../components/InputBar";
import axios from "axios";
import "../App.css";
import { getToken, getActiveChatId } from "../utils/session";

import { API_BASE } from "../utils/apiConfig";

function ChatPage({ onAdmin }) {
  const [messages, setMessages] = useState([]);
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 1100px)").matches
  );
  const [isTyping, setIsTyping] = useState(false);

  // ✅ THIS is the correct chatId state
  const [chatId, setChatId] = useState(getActiveChatId());
  const [mode, setMode] = useState("normal");

  const token = getToken();

  useEffect(() => {
    const narrowWindow = window.matchMedia("(max-width: 1100px)");
    const handleWindowWidth = (event) => setCollapsed(event.matches);

    narrowWindow.addEventListener("change", handleWindowWidth);
    return () => narrowWindow.removeEventListener("change", handleWindowWidth);
  }, []);

  /* =========================
     ENSURE CHAT EXISTS
  ========================= */
  const ensureChat = async () => {
    if (chatId) return chatId;

    try {
      const res = await axios.post(
        `${API_BASE}/chats`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newChatId = res.data._id;
      setChatId(newChatId);
      
      // Persist to session
      sessionStorage.setItem("logicline_active_chat", newChatId);

      // Notify Sidebar
      window.dispatchEvent(
        new CustomEvent("chatCreated", { detail: res.data })
      );

      return newChatId;
    } catch (err) {
      console.error("Failed to create chat", err);
      return null;
    }
  };

  /* =========================
     LOAD CHAT FROM BACKEND WHEN chatId CHANGES
  ========================= */
  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      return;
    }

    if (!token) return;

    axios
      .get(`${API_BASE}/chats/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const backendMessages = res.data.messages.map((m) => ({
          sender: m.role === "user" ? "user" : "ai",
          text: m.content,
          sourceType: m.sourceType,
          guardrailStatus: m.guardrailStatus,
          taskType: m.taskType,
          citations: m.citations || [],
        }));
        console.log("backendMessages", backendMessages);
        setMessages(backendMessages);
        setMode(res.data.mode || "normal");
      })
      .catch((err) => {
        console.error("Load chat failed", err);
      });
  }, [chatId, token]);

  /* =========================
     SEND MESSAGE
  ========================= */
  const handleSendMessage = async (text) => {
  if (!text.trim()) return;

  const userMsg = { sender: "user", text };
  setMessages((prev) => [...prev, userMsg]);

  if (!chatId) {
    setMessages((prev) => [
      ...prev,
      { sender: "ai", text: "Please upload a relevant document before starting an analysis.", sourceType: "not_supported", guardrailStatus: "no_document" },
    ]);
    return;
  }

  setIsTyping(true);

  try {
    const res = await axios.post(
      `${API_BASE}/chats/${chatId}/ask`,
      { question: text },
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      }
    );

    // Update chat title in the sidebar
    if (res.data.title) {
      window.dispatchEvent(
        new CustomEvent("chatTitleUpdated", {
          detail: { chatId, title: res.data.title },
        })
      );
    }

    setMessages((prev) => [
      ...prev,
      {
        sender: "ai",
        text: res.data.answer,
        sourceType: res.data.sourceType,
        guardrailStatus: res.data.guardrailStatus,
        taskType: res.data.taskType,
        citations: res.data.citations || [],
        requestId: res.data.requestId,
      },
    ]);
    if (res.data.mode) setMode(res.data.mode);

  } catch (err) {
    console.error("ASK ERROR", err);

    let errorMessage = "LogicLine could not complete this request. Please try again.";

    // Backend sent structured error
    if (err?.response?.data?.message) {
      errorMessage = err.response.data.message;
    }
    // Backend sent a generic error (like error: "No documents uploaded")
    else if (err?.response?.data?.error) {
      errorMessage = err.response.data.error;
    }
    // Network timeout
    else if (err.code === "ECONNABORTED") {
      errorMessage = "The analysis timed out. Please retry, or use a smaller set of documents.";
    }
    // No response from server at all
    else if (!err.response) {
      errorMessage = "LogicLine is temporarily unreachable. Your existing conversation is safe; please try again.";
    }

    setMessages((prev) => [
      ...prev,
      { sender: "ai", text: errorMessage },
    ]);

  } finally {
    setIsTyping(false);
  }
};


  return (
    <div className="app">
      <Sidebar
        collapsed={collapsed}
        toggleSidebar={() => setCollapsed((p) => !p)}
        onChatSelect={setChatId}   // ✅ Sidebar now updates ChatPage
      />

      <div className="main-area chat-mode">
        <ChatWindow
          messages={messages}
          isTyping={isTyping}
          chatId={chatId}
          mode={mode}
          onEnsureChat={ensureChat}
          onSend={handleSendMessage}
          onAdmin={onAdmin}
        />
        <InputBar onSend={handleSendMessage} disabled={isTyping} />
      </div>
    </div>
  );
}

export default ChatPage;
